import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { apiFetch, syncTenant } from '../api';

const TenantDataContext = createContext();

// A tenant left open for hours never re-triggers fetchAll on its own (no
// prop/state change causes that), so without this a long session would
// only ever see the one sync from when it was first opened. The backend
// throttles the actual Graph pull to once per hour per tenant regardless
// (see handleSync in app.js), so polling on this same cadence just means a
// continuously open tab picks up that hourly resync instead of missing it.
const REFRESH_INTERVAL_MS = 60 * 60 * 1000;

// readOnly: the server's browser making a report PDF only reads. It never starts a sync and does not poll.
export function TenantDataProvider({ tenantId, children, readOnly = false }) {
  const [tenant, setTenant] = useState(null);
  const [secureScore, setSecureScore] = useState(null);
  const [mfa, setMfa] = useState(null);
  const [ca, setCa] = useState(null);
  const [membership, setMembership] = useState(null);
  const [devices, setDevices] = useState(null);
  const [applications, setApplications] = useState(null);
  const [resources, setResources] = useState(null);
  const [sharePointSettings, setSharePointSettings] = useState(null);
  const [security, setSecurity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [errors, setErrors] = useState({});
  const gidRef = useRef(null);

  const loadTenantData = useCallback(async (gid) => {
    const [scoreRes, mfaRes, caRes, membershipRes, devicesRes, appsRes, resourcesRes, spSettingsRes, securityRes] = await Promise.allSettled([
      apiFetch(`/api/secure-score?tenantId=${gid}`).then((r) => r.json()),
      apiFetch(`/api/mfa-status?tenantId=${gid}`).then((r) => r.json()),
      apiFetch(`/api/conditional-access?tenantId=${gid}`).then((r) => r.json()),
      apiFetch(`/api/membership?tenantId=${gid}`).then((r) => r.json()),
      apiFetch(`/api/devices?tenantId=${gid}`).then((r) => r.json()),
      apiFetch(`/api/applications?tenantId=${gid}`).then((r) => r.json()),
      apiFetch(`/api/tenant-resources?tenantId=${gid}`).then((r) => r.json()),
      apiFetch(`/api/sharepoint-settings?tenantId=${gid}`).then((r) => r.json()),
      apiFetch(`/api/security-overview?tenantId=${gid}`).then((r) => r.json()),
    ]);

    const nextErrors = {};

    if (scoreRes.status === 'fulfilled' && scoreRes.value.currentScore !== undefined) setSecureScore(scoreRes.value);
    else nextErrors.secureScore = true;

    if (mfaRes.status === 'fulfilled' && mfaRes.value.totalUsers !== undefined) setMfa(mfaRes.value);
    else nextErrors.mfa = true;

    if (caRes.status === 'fulfilled' && caRes.value.totalPolicies !== undefined) setCa(caRes.value);
    else nextErrors.ca = true;

    if (membershipRes.status === 'fulfilled' && Array.isArray(membershipRes.value.users)) setMembership(membershipRes.value);
    else nextErrors.membership = true;

    if (devicesRes.status === 'fulfilled' && devicesRes.value.totalDevices !== undefined) setDevices(devicesRes.value);
    else nextErrors.devices = true;

    if (appsRes.status === 'fulfilled' && appsRes.value.totalApplications !== undefined) setApplications(appsRes.value);
    else nextErrors.applications = true;

    if (resourcesRes.status === 'fulfilled' && resourcesRes.value.teamsCount !== undefined) setResources(resourcesRes.value);
    else nextErrors.resources = true;

    if (spSettingsRes.status === 'fulfilled' && spSettingsRes.value.sharingCapability !== undefined) setSharePointSettings(spSettingsRes.value);
    else nextErrors.sharePointSettings = true;

    if (securityRes.status === 'fulfilled' && securityRes.value.openIncidentCount !== undefined) setSecurity(securityRes.value);
    else nextErrors.security = true;

    setErrors(nextErrors);
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);

    const tenantsRes = await apiFetch(`/api/tenants?tenantId=${tenantId}`).then((r) => r.json()).catch(() => null);
    const found = Array.isArray(tenantsRes) ? tenantsRes.find((t) => t.id === tenantId) : null;
    setTenant(found || null);

    if (!found) {
      setErrors({ tenant: true });
      setLoading(false);
      return;
    }

    // found.tenantId is the Azure AD tenant GUID the Graph-backed endpoints
    // expect — distinct from the route param above only in name, since the
    // Worker sets them to the same value on tenant creation, but this is the
    // semantically correct field to pass through.
    const gid = found.tenantId;
    gidRef.current = gid;

    // The backend throttles this to at most once per hour per tenant (see
    // handleSync/SYNC_THROTTLE_MS in app.js): a tenant nobody has opened in
    // over an hour gets a real resync here, one someone looked at minutes
    // ago (even in a different tab, or after a logout/login) is a cheap
    // no-op that leaves the existing cache — and whatever's already on
    // screen — untouched.
    // What is already stored is shown at once, however old it is: the backend serves it without waiting on Microsoft.
    await loadTenantData(gid);
    setLoading(false);

    // The refresh from Microsoft then runs in the background (still once per hour per tenant, see above). When it did
    // real work, the page quietly picks up the new figures.
    if (readOnly) return;
    setIsSyncing(true);
    const synced = await syncTenant(gid).catch(() => null);
    setIsSyncing(false);
    if (synced && !synced.skipped && !synced.error) await loadTenantData(gid).catch(() => {});
  }, [tenantId, loadTenantData, readOnly]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // The first read of a company's devices answers within seconds with the list, and Intune's per-device states follow in the
  // background (statesComplete false). Ask again every few seconds until they are all in; the answer then replaces the partial one.
  const devicesPending = !readOnly && devices?.statesComplete === false;
  useEffect(() => {
    if (!devicesPending) return undefined;
    let tries = 0;
    const timer = setInterval(async () => {
      const gid = gidRef.current;
      if (!gid || tries >= 40) { clearInterval(timer); return; }
      tries += 1;
      try {
        const next = await apiFetch(`/api/devices?tenantId=${gid}`).then((r) => r.json());
        if (next && next.totalDevices !== undefined) setDevices(next);
      } catch { /* the next try */ }
    }, 6000);
    return () => clearInterval(timer);
  }, [devicesPending]);

  // Keep a long-lived, continuously open session fresh: check in on the
  // backend's hourly schedule instead of only ever syncing once on the
  // initial page load. Deliberately doesn't touch `loading` — this must
  // stay a quiet background refresh, not a full loading-state flash for
  // someone actively using the page.
  useEffect(() => {
    if (readOnly) return undefined;
    const interval = setInterval(async () => {
      const gid = gidRef.current;
      if (!gid) return;
      await syncTenant(gid).catch(() => {});
      await loadTenantData(gid).catch(() => {});
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadTenantData, readOnly]);

  return (
    <TenantDataContext.Provider
      value={{ tenant, secureScore, mfa, ca, membership, devices, applications, resources, sharePointSettings, security, loading, isSyncing, errors, refetch: fetchAll }}
    >
      {children}
    </TenantDataContext.Provider>
  );
}

export function useTenantData() {
  const ctx = useContext(TenantDataContext);
  if (!ctx) {
    throw new Error('useTenantData must be used within TenantDataProvider');
  }
  return ctx;
}
